import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { CloudRain, Loader2 } from 'lucide-react';
import { useSelector } from 'react-redux';
import { Switch } from '@/lib/components/ui/switch';
import { useAppDispatch } from '@/data/store/utils/redux';
import { getNaerasSettingsAsync, updateNaerasSettingsAsync } from '@/data/store/actions';
import { getNaerasSettings } from '@/data/store/selectors';
import useNotify from '@/hooks/useNotify';
import { naerasWriteErrorMessage, wetSeasonWindowLabel } from '../utils/naerasBid';

/**
 * §1.6 — «Υγρή περίοδος». One stored switch that only matters to bids computed
 * from now on: the daily Job B, the Calculate button, and finalizing an
 * unedited future day. It can only close the wet season, never open it, so it
 * is drawn from `effective` / `editable` rather than the stored position —
 * outside 1/10–15/4 that shows it off and locked.
 */
export const WetSeasonSwitch: React.FC = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useNotify();
  const settings = useSelector(getNaerasSettings);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    dispatch(getNaerasSettingsAsync({ silent: true }))
      .unwrap()
      .catch(() => undefined);
  }, [dispatch]);

  const handleChange = async (wetSeason: boolean) => {
    setIsSaving(true);

    try {
      await dispatch(updateNaerasSettingsAsync({ wetSeason, silent: true })).unwrap();
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Ο διακόπτης δεν άλλαξε',
        message: naerasWriteErrorMessage(error, 'Η ρύθμιση υγρής περιόδου δεν αποθηκεύτηκε.'),
      });
      // The window may have closed since the last read — show what the server holds
      dispatch(getNaerasSettingsAsync({ silent: true }))
        .unwrap()
        .catch(() => undefined);
    } finally {
      setIsSaving(false);
    }
  };

  const appliesTo = settings?.applies_to
    ? format(parseISO(settings.applies_to), 'dd/MM/yyyy')
    : null;

  const hint = !settings
    ? 'Φόρτωση ρύθμισης…'
    : settings.editable
    ? `Ισχύει από την προσφορά της ${appliesTo}`
    : `Εκτός υγρής περιόδου (${wetSeasonWindowLabel(settings.window)})`;

  return (
    <label
      className="flex items-center gap-3 rounded-2xl border border-blue-200 bg-white px-4 py-2 shadow-sm"
      title="Κλείστε τον διακόπτη όταν οι βροχές δεν έχουν αρχίσει ακόμη. Η αλλαγή αφορά την επόμενη προσφορά που θα υπολογιστεί."
    >
      <CloudRain className={`h-4 w-4 ${settings?.effective ? 'text-blue-600' : 'text-gray-400'}`} />
      <span className="flex flex-col leading-tight">
        <span className="text-sm font-semibold text-gray-800">Υγρή περίοδος</span>
        <span className="text-[11px] text-gray-500">{hint}</span>
      </span>
      {isSaving ? (
        <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
      ) : (
        <Switch
          checked={Boolean(settings?.effective)}
          disabled={!settings?.editable || isSaving}
          onCheckedChange={handleChange}
        />
      )}
    </label>
  );
};

export default WetSeasonSwitch;
